import * as React from 'react';
import cn from 'classnames';

import { useTheme } from '../../theme';
import OrderPreview from './OrderPreview/OrderPreview';
import OrderStatus from './OrderStatus/OrderStatus';
import OrderBooking from './OrderBooking/OrderBooking';
import OrderPassengers from './OrderPassengers/OrderPassengers';
import OrderCardControls from './OrderCardControls/OrderCardControls';
import { MOBILE_MIN_WIDTH } from '../../utils';
import { OrderCardType } from '../types';
import { AviaOrder, OrderStatus as OrderStatusType } from '@websky/graphql';
import OrderTickets from './OrderTickets/OrderTickets';
import { BaseButton } from '../../BaseComponents';
import PaymentStatus from './PaymentStatus/PaymentStatus';
import MediaQuery from 'react-responsive';
import { useTranslation } from 'react-i18next';
import OrderChange from './OrderChange/OrderChange';
import { useRenderers } from '../../renderProps';
import { useConfig } from '../../context';
import DeleteOrderModal from './DeleteOrderModal/DeleteOrderModal';
import { CheckinCard } from '@shared/CheckinCard';

export interface Props {
	order: AviaOrder;
	isCheckinOpen?: boolean;
	type: OrderCardType;
	hidePrice?: boolean;
	renderControls?: () => React.ReactElement;
	onDownloadClick?: () => void;
	onTransactionsClick?: () => void;
	onClick?: (orderId: string) => void;
	goToExchange?: () => void;
	goToRefund?: () => void;
	goToRefundServices?: () => void;
	goToChangePassenger?: (passengerId: string) => void;
	accountOrderAdditionalItem?: () => React.ReactNode;
	accountOrderAdditionalButtons?: () => React.ReactNode;
	onDeleted?: () => void;
}

export const OrderCardContext = React.createContext<Props>(null);

const OrderCard: React.FC<Props> = props => {
	const { type, accountOrderAdditionalItem, accountOrderAdditionalButtons } = props;
	const { OrderCard: css } = useTheme('OrderCard');
	const { t } = useTranslation('OrderCard');
	const {
		renderOrderCardStatus: RenderOrderCardStatusOverride = OrderStatus,
		renderOrderCardBooking: RenderOrderCardBookingOverride = OrderBooking
	} = useRenderers();
	const {
		global: {
			reserveEngines,
			companyInfo: { iataCode }
		}
	} = useConfig();

	const isArmeniaOrGeorgian = iataCode === 'RM' || iataCode === 'A9';

	const onClickHandler = () => {
		if (typeof props.onClick === 'function') {
			props.onClick(props.order.id);
		}
	};

	const exchangeAvailable = props.order.exareInfo?.exchangeAvailability;
	const refundAvailable = props.order.exareInfo?.refundAvailability;
	const refundServicesAvailable = props.order.exareInfo?.gdsServicesRefundAvailability;

	const onOrderChangeClick = (buttonType: 'exchange' | 'refund' | 'refundServices') => {
		if (buttonType === 'exchange') {
			props.goToExchange?.();
		} else if (buttonType === 'refundServices') {
			props.goToRefundServices?.();
		} else {
			props.goToRefund?.();
		}
	};

	return (
		<OrderCardContext.Provider value={props}>
			<CheckinCard
				className={cn(css.container, css.root__checkinCard, {
					[css.type_review]: type === 'review',
					[css.type_account]: type === 'account'
				})}
			>
				<OrderPreview className={css.preview} onClick={props.onClick && onClickHandler} />

				<div
					className={cn(css.root, { [css.orderConfirmed]: props.order.status === OrderStatusType.Confirmed })}
				>
					{type === 'review' && (
						<>
							<RenderOrderCardStatusOverride className={cn(css.item, css.status)} />

							<PaymentStatus />

							<RenderOrderCardBookingOverride
								className={cn(css.item, css.booking)}
								hidePrice={props.hidePrice}
								onDownloadTicket={props.onDownloadClick}
							/>

							{(!reserveEngines?.redirectToWebsky1 || !isArmeniaOrGeorgian) && <OrderCardControls />}

							<OrderPassengers
								className={cn(css.item, css.passengers)}
								goToChangePassenger={props.goToChangePassenger}
							/>

							{props.order.status === OrderStatusType.Confirmed && type === 'review' && (
								<OrderTickets className={cn(css.item, css.tickets)} />
							)}

							{(exchangeAvailable || refundAvailable || refundServicesAvailable) && (
								<OrderChange
									onClick={onOrderChangeClick}
									exchangeAvailable={exchangeAvailable}
									refundAvailable={refundAvailable}
									refundServicesAvailable={refundServicesAvailable}
								/>
							)}
						</>
					)}

					{type === 'account' && (
						<>
							{/* OrderBooking */}
							{props.order.status === OrderStatusType.Confirmed && (
								<RenderOrderCardStatusOverride className={cn(css.item, css.status)} />
							)}
							<OrderBooking className={cn(css.item, css.booking)} hidePrice={props.hidePrice} />
							<OrderPassengers className={cn(css.item, css.passengers)} />
							<PaymentStatus />
							{accountOrderAdditionalItem?.()}
							<div className={css.buttons}>
								<DeleteOrderModal
									orderId={props.order.id}
									orderLocator={props.order.locator}
									onDeleted={props.onDeleted}
								/>
								<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
									{accountOrderAdditionalButtons?.()}
									<BaseButton
										variant="smallPrimary"
										className={css.openOrder}
										onClick={onClickHandler}
									>
										{t('Open')}
									</BaseButton>
								</MediaQuery>
							</div>
						</>
					)}
				</div>
			</CheckinCard>
		</OrderCardContext.Provider>
	);
};

export default OrderCard;
